import { Tween, Easing } from '@tweenjs/tween.js';
import AnimationClip from '../AnimationClip';
import Point from '../../../Utility/Point';
import GameObject from '../../../GameObject/GameObject';
import PIXIRenderer from '../../Render/PIXIRenderer';
import { MathHelper } from '../../../Utility/MathHelper';
import { Random } from '../../../Utility/Random';
import ElementComponent from '../../../Component/UI/Element';

export interface ICropRewardTranslateParams {
    destination: { x: number, y: number };
    duration: number;
}

export class CropRewardTranslate extends AnimationClip {
    public rewardsTranslated: number = 0;
    public isPlaying: boolean = true;

    public *execute(
        gameObject: GameObject,
        meta?: ICropRewardTranslateParams
    ): IterableIterator<number> {
        try {
            const element: ElementComponent = gameObject.getComponent(
                ElementComponent
            );
            const tween: Tween = this.createBezierTween(
                element,
                meta.destination,
                meta.duration
            );
            tween
                .onComplete(() => {
                    this.isPlaying = false;
                    gameObject.destroy();
                })
                .start(this.elapsedTime);

            while (this.isPlaying) {
                yield;
            }
        } catch (e) {
            console.log(e);
        }
    }

    protected createBezierTween(
        element: ElementComponent,
        destination: { x: number, y: number },
        duration: number = 2500
    ): Tween {
        const bezierTween = this.getTween({ progress: 0 });
        const center = new Point(
            PIXIRenderer.mapViewport.screenWidth / 2,
            PIXIRenderer.mapViewport.screenHeight / 2
        );
        const p1 = Point.fromVector4(element.offsets).clone();
        const p4 = new Point(-destination.x, -destination.y);
        const p3 = new Point(
            -(
                center.x +
                PIXIRenderer.mapViewport.screenWidth / 2 +
                Random.value(-1, 1) * (PIXIRenderer.mapViewport.screenWidth / 4)
            ),
            -(
                center.y +
                Random.value(-1, 1) *
                    (PIXIRenderer.mapViewport.screenHeight / 4)
            )
        );
        const p2 = new Point(
            -(Random.sign() > 0
                ? center.x -
                  PIXIRenderer.mapViewport.screenWidth / 2 +
                  Random.value(-1, 1) *
                      (PIXIRenderer.mapViewport.screenWidth / 2)
                : center.x +
                  PIXIRenderer.mapViewport.screenWidth / 2 +
                  Random.value(-1, 1) *
                      (PIXIRenderer.mapViewport.screenWidth / 2)),
            -(
                center.y +
                PIXIRenderer.mapViewport.screenHeight / 1.5 +
                Random.value(-1, 1) *
                    (PIXIRenderer.mapViewport.screenHeight / 6)
            )
        );

        const rotationDirection: number = Random.sign();
        const distance: number = Math.abs(Point.distance(p1, p4));
        const delay: number =
            Math.max(distance - PIXIRenderer.mapViewport.screenWidth, 1000) / 2;

        duration = Random.value(0.5, 1) * duration + delay;

        bezierTween
            .to({ progress: 1 }, duration)
            .onUpdate(t => {
                const xy = MathHelper.cubicBezier(
                    t.progress,
                    p1,
                    p2,
                    p3,
                    p4
                );
                element.offsets = [xy.x, xy.y, xy.x, xy.y];
                element.rotation += rotationDirection * (Math.PI / 180) * 7;
            })
            .easing(Easing.Sinusoidal.In);

        return bezierTween;
    }
}
